import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Store, Users, GraduationCap, Sparkles, ShieldCheck, TrendingUp, Package, Headphones, Star, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { api } from "@/lib/api";
import { toast } from "sonner";

const HERO_IMG = "https://images.unsplash.com/photo-1781559818983-c32838ee3d55?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxzaG9wa2VlcGVyJTIwdXNpbmclMjB0YWJsZXQlMjB3YXJlaG91c2V8ZW58MHx8fHwxNzg1NjAxNzgzfDA&ixlib=rb-4.1.0&q=85";
const FREEL_IMG = "https://images.unsplash.com/photo-1541560052-5e137f229371?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHw0fHxmcmVlbGFuY2VyJTIwd29ya2luZyUyMGxhcHRvcCUyMG1vZGVybnxlbnwwfHx8fDE3ODU2MDE3ODN8MA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <Hero />
      <TrustBar />
      <About />
      <Features />
      <HowItWorks />
      <Benefits />
      <Testimonials />
      <FAQ />
      <Contact />
      <Footer />
    </div>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden grid-backdrop">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-16 grid lg:grid-cols-12 gap-10 items-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="lg:col-span-6">
          <div className="eyebrow mb-4">E-commerce workforce platform</div>
          <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#0F172A]">
            Hire <span className="text-brand">trained freelancers</span> to run your shop on Amazon, Flipkart & Meesho.
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
            SELLEXR connects local shopkeepers with a certified workforce — students, housewives, and professionals — to manage listings, inventory, orders and support.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/signup?role=shopkeeper"><Button data-testid="hero-hire-btn" className="btn-orange hover:bg-[#FF6A00] px-7 py-6 text-base">Hire a freelancer <ArrowRight size={18} className="ml-2" /></Button></Link>
            <Link to="/signup?role=freelancer"><Button variant="outline" data-testid="hero-become-btn" className="px-7 py-6 text-base border-slate-300">Become a freelancer</Button></Link>
          </div>
          <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg">
            {[["800+", "Freelancers"], ["250+", "Shopkeepers"], ["42", "Cities"]].map(([n, l]) => (
              <div key={l}>
                <div className="font-heading font-bold text-2xl text-brand">{n}</div>
                <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">{l}</div>
              </div>
            ))}
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.1 }} className="lg:col-span-6 relative">
          <div className="relative rounded-3xl overflow-hidden border border-slate-200/70 shadow-[0_24px_80px_rgba(21,94,239,0.15)]">
            <img src={HERO_IMG} alt="Shopkeeper managing store on tablet" className="w-full h-[520px] object-cover" />
          </div>
          <div className="absolute -bottom-6 -left-6 card-soft p-4 flex items-center gap-3 max-w-[240px] hidden md:flex">
            <div className="w-10 h-10 rounded-lg bg-[#FF6A00]/10 flex items-center justify-center"><TrendingUp className="text-[#FF6A00]" size={20} /></div>
            <div>
              <div className="text-xs text-slate-500">Avg sales lift</div>
              <div className="font-heading font-bold text-lg">+62% in 90 days</div>
            </div>
          </div>
          <div className="absolute -top-6 -right-6 card-soft p-4 flex items-center gap-3 max-w-[220px] hidden md:flex">
            <div className="w-10 h-10 rounded-lg bg-[#155EEF]/10 flex items-center justify-center"><Sparkles className="text-[#155EEF]" size={20} /></div>
            <div>
              <div className="text-xs text-slate-500">AI matched</div>
              <div className="font-heading font-bold text-sm">Top 3 freelancers, instantly</div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function TrustBar() {
  const items = ["Amazon", "Flipkart", "Meesho", "JioMart", "Ajio", "Snapdeal"];
  return (
    <div className="border-y border-slate-200/70 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-wrap items-center justify-center gap-x-12 gap-y-4">
        <span className="text-xs uppercase tracking-[0.22em] text-slate-500">Works with</span>
        {items.map((n) => (
          <span key={n} className="font-heading font-semibold text-slate-400 hover:text-slate-700 transition-colors duration-200">{n}</span>
        ))}
      </div>
    </div>
  );
}

function About() {
  return (
    <section id="about" className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-5">
        <div className="eyebrow">About SELLEXR</div>
        <h2 className="mt-3 font-heading font-bold text-3xl lg:text-4xl leading-tight">The workforce layer for Indian e-commerce.</h2>
        <p className="mt-6 text-slate-600 leading-relaxed">
          Millions of local shopkeepers struggle to keep up with online marketplaces. SELLEXR trains everyday people — students, housewives, job-seekers — into certified e-commerce operators and matches them with shops that need help.
        </p>
        <p className="mt-4 text-slate-600 leading-relaxed">
          Everyone wins: shopkeepers grow online sales, freelancers earn from home, and marketplaces get healthier listings.
        </p>
      </div>
      <div className="lg:col-span-7 grid grid-cols-2 gap-4">
        {[
          { icon: Store, title: "For shopkeepers", body: "Get certified help to grow online — without hiring full-time." },
          { icon: Users, title: "For freelancers", body: "Earn from home with structured training and steady clients." },
          { icon: GraduationCap, title: "Training academy", body: "Free courses, quizzes, and certificates in Indian e-commerce ops." },
          { icon: Sparkles, title: "AI matching", body: "Claude-powered matching of the right freelancer to your shop." },
        ].map((c) => (
          <div key={c.title} className="card-soft p-6">
            <div className="w-10 h-10 rounded-lg bg-[#155EEF]/10 flex items-center justify-center mb-4"><c.icon className="text-brand" size={20} /></div>
            <div className="font-heading font-semibold">{c.title}</div>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">{c.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Features() {
  const feats = [
    { icon: Package, title: "Product listing", body: "Optimized titles, bullets, A+ content and image guidelines that convert." },
    { icon: TrendingUp, title: "Catalog SEO", body: "Rank higher on Amazon, Flipkart & Meesho search with proven keyword frameworks." },
    { icon: Store, title: "Inventory sync", body: "Real-time stock visibility across every marketplace you sell on." },
    { icon: Headphones, title: "Customer support", body: "Trained freelancers handle queries, returns and reviews with empathy." },
    { icon: ShieldCheck, title: "Verified & certified", body: "Every freelancer is trained, tested, and issued a SELLEXR certificate." },
    { icon: Sparkles, title: "AI client matching", body: "Claude Sonnet 5 matches your needs to the top three freelancers in seconds." },
  ];
  return (
    <section id="features" className="bg-slate-50 py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl">
          <div className="eyebrow">Features</div>
          <h2 className="mt-3 font-heading font-bold text-3xl lg:text-4xl leading-tight">Everything a modern seller needs.</h2>
        </div>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {feats.map((f) => (
            <motion.div key={f.title} whileHover={{ y: -4 }} transition={{ duration: 0.2 }} className="card-soft p-7">
              <div className="w-11 h-11 rounded-xl bg-[#155EEF]/10 flex items-center justify-center mb-5"><f.icon className="text-brand" size={22} /></div>
              <div className="font-heading font-semibold text-lg">{f.title}</div>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    { n: "01", t: "Sign up & verify", b: "Shopkeepers register their store. Freelancers register and train through the SELLEXR Academy." },
    { n: "02", t: "AI match", b: "Our matcher suggests the top freelancers based on your marketplace, category, and city." },
    { n: "03", t: "Hire & assign tasks", b: "Add tasks like listing, orders, or support. Your freelancer executes and updates progress." },
    { n: "04", t: "Track & pay", b: "See tasks completed, sales lift, and reviews. Approve payments from the dashboard." },
  ];
  return (
    <section id="how" className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <div className="eyebrow">How it works</div>
          <h2 className="mt-3 font-heading font-bold text-3xl lg:text-4xl leading-tight">From signup to your first sale, in days.</h2>
          <p className="mt-4 text-slate-600">A clean, structured flow so you never feel lost.</p>
        </div>
        <div className="lg:col-span-8 space-y-4">
          {steps.map((s, i) => (
            <motion.div key={s.n} initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.4, delay: i * 0.1 }} viewport={{ once: true }} className="card-soft p-6 flex gap-6 items-start">
              <div className="font-heading font-bold text-3xl text-[#FF6A00]">{s.n}</div>
              <div>
                <div className="font-heading font-semibold text-lg">{s.t}</div>
                <p className="mt-1 text-slate-600">{s.b}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Benefits() {
  return (
    <section id="benefits" className="bg-[#0F172A] text-white py-20 lg:py-28 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6">
          <div className="eyebrow" style={{ color: "#FF6A00" }}>Benefits</div>
          <h2 className="mt-3 font-heading font-bold text-3xl lg:text-4xl leading-tight">Grow your business without growing your headcount.</h2>
          <ul className="mt-8 space-y-4">
            {[
              "Reduce your operational cost by 60% vs. full-time hires",
              "Onboard a certified operator in under 48 hours",
              "Multi-marketplace management from one dashboard",
              "Transparent ratings, reviews, and secure payments",
              "Free training academy included for all freelancers",
            ].map((b) => (
              <li key={b} className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-[#FF6A00]/20 flex items-center justify-center flex-shrink-0 mt-0.5"><div className="w-2 h-2 rounded-full bg-[#FF6A00]" /></div>
                <span className="text-slate-200">{b}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-6 relative">
          <img src={FREEL_IMG} alt="Certified freelancer at work" className="rounded-3xl w-full h-[440px] object-cover border border-white/10" />
          <div className="absolute -bottom-4 -left-4 card-soft p-4 flex items-center gap-3 max-w-[240px] bg-white text-slate-900">
            <div className="w-10 h-10 rounded-lg bg-[#155EEF]/10 flex items-center justify-center"><GraduationCap className="text-[#155EEF]" size={20} /></div>
            <div>
              <div className="text-xs text-slate-500">Certified freelancers</div>
              <div className="font-heading font-bold">Trained by SELLEXR Academy</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  const t = [
    { name: "Ravi Sharma", role: "Shopkeeper, Jaipur", body: "In 60 days my Amazon listings went from invisible to top of category. My SELLEXR freelancer knows the platform better than I ever could.", rating: 5 },
    { name: "Priya Verma", role: "Freelancer, Bengaluru", body: "As a mom, I needed flexible work. SELLEXR gave me training, certification and clients. I earn ₹25k/month from home.", rating: 5 },
    { name: "Manoj Iyer", role: "D2C Founder, Kochi", body: "The AI matcher paired us with a Meesho specialist in minutes. Orders doubled in the first month.", rating: 5 },
  ];
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
      <div className="eyebrow">Testimonials</div>
      <h2 className="mt-3 font-heading font-bold text-3xl lg:text-4xl">Loved by shopkeepers and freelancers alike.</h2>
      <div className="mt-12 grid md:grid-cols-3 gap-6">
        {t.map((x) => (
          <div key={x.name} className="card-soft p-7">
            <div className="flex gap-1">
              {Array.from({ length: x.rating }).map((_, i) => <Star key={i} size={16} className="fill-[#FF6A00] text-[#FF6A00]" />)}
            </div>
            <p className="mt-4 text-slate-700 leading-relaxed">"{x.body}"</p>
            <div className="mt-6 pt-6 border-t border-slate-100">
              <div className="font-heading font-semibold">{x.name}</div>
              <div className="text-sm text-slate-500">{x.role}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function FAQ() {
  const qs = [
    { q: "Who can become a SELLEXR freelancer?", a: "Anyone 18+ — students, housewives, job-seekers or professionals. You'll complete our free academy training and quiz to be certified." },
    { q: "How does the AI matching work?", a: "We use Claude Sonnet 5 to analyze your marketplace, category, city and needs, then rank the top freelancers by skills, rating and fit." },
    { q: "What marketplaces are supported?", a: "Amazon, Flipkart, Meesho, JioMart, Ajio, Snapdeal — with more added regularly." },
    { q: "How are payments handled?", a: "Shopkeepers log payments in the dashboard. Freelancers see earnings in real-time. Ratings & reviews keep the ecosystem trusted." },
    { q: "Is training free?", a: "Yes — every freelancer gets free access to the SELLEXR Academy with videos, quizzes and certificates." },
  ];
  return (
    <section id="faq" className="bg-slate-50 py-20 lg:py-28">
      <div className="max-w-4xl mx-auto px-6 lg:px-10">
        <div className="eyebrow">FAQ</div>
        <h2 className="mt-3 font-heading font-bold text-3xl lg:text-4xl">Everything you wanted to know.</h2>
        <Accordion type="single" collapsible className="mt-10">
          {qs.map((x, i) => (
            <AccordionItem key={i} value={`i${i}`} className="border-b border-slate-200">
              <AccordionTrigger data-testid={`faq-${i}`} className="text-left font-heading font-semibold py-5 hover:no-underline">{x.q}</AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed">{x.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Thanks — we'll be in touch shortly.");
      setForm({ name: "", email: "", message: "" });
    } catch { toast.error("Something went wrong"); }
    finally { setLoading(false); }
  };
  return (
    <section id="contact" className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-5">
        <div className="eyebrow">Contact</div>
        <h2 className="mt-3 font-heading font-bold text-3xl lg:text-4xl">Let's grow your online business.</h2>
        <p className="mt-4 text-slate-600">Questions, partnerships or bulk hiring — drop us a line and our team will get back within one business day.</p>
        <div className="mt-8 space-y-3 text-sm text-slate-700">
          <div><span className="font-semibold">Support:</span> care@sellexr.com</div>
          <div><span className="font-semibold">Partnerships:</span> hello@sellexr.com</div>
        </div>
      </div>
      <form onSubmit={submit} className="lg:col-span-7 card-soft p-8 space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Name</label>
            <Input data-testid="contact-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Email</label>
            <Input data-testid="contact-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" />
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Message</label>
          <Textarea data-testid="contact-message" required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5" />
        </div>
        <Button data-testid="contact-submit" disabled={loading} className="btn-orange hover:bg-[#FF6A00] w-full md:w-auto px-8 py-6">
          {loading ? "Sending..." : "Send message"}
        </Button>
      </form>
    </section>
  );
}
